import { AUDIT_BASE_URL } from "../../utils/ApiUrls";

class AuditHistoryService {
  /**
   * Get recent audit history for Audit Manager dashboard
   * Returns 5 most recent records from each audit history model
   * @param {string} accessToken - JWT access token
   */
  async getRecentAuditHistory(accessToken) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/recent/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get recent audit history for Audit User dashboard
   * Returns 5 most recent records from each audit history model
   * Filtered by user's audit plans and owned recommendations
   * @param {string} accessToken - JWT access token
   */
  async getRecentAuditUserHistory(accessToken) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/audit-user/recent/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit user history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }


  // ==================== AUDIT PROGRAM HISTORY (AuditManager only) ====================
  
  /**
   * List audit program history for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listAuditProgramHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/programs/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit program history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get audit program history detail for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getAuditProgramHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/programs/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit program history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // ==================== AUDIT PLAN HISTORY ====================
  
  /**
   * List audit plan history for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listAuditPlanHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/plans/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit plan history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get audit plan history detail for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getAuditPlanHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/plans/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit plan history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * List my audit plan history for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listMyAuditPlanHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/my-plans/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my audit plan history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get my audit plan history detail for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getMyAuditPlanHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/my-plans/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my audit plan history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // ==================== AUDIT SECTION HISTORY ====================
  
  /**
   * List audit section history for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listAuditSectionHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/sections/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit section history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get audit section history detail for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getAuditSectionHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/sections/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit section history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * List my audit section history for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listMyAuditSectionHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/my-sections/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my audit section history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get my audit section history detail for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getMyAuditSectionHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/my-sections/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my audit section history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // ==================== AUDIT QUESTION HISTORY ====================
  
  /**
   * List audit question history for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listAuditQuestionHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/questions/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit question history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get audit question history detail for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getAuditQuestionHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/questions/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch audit question history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * List my audit question history for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listMyAuditQuestionHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/my-questions/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my audit question history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get my audit question history detail for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getMyAuditQuestionHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/my-questions/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my audit question history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // ==================== RECOMMENDATION ACTION HISTORY ====================
  
  /**
   * List recommendation action history for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listRecommendationActionHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/recommendations/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch recommendation action history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get recommendation action history detail for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getRecommendationActionHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/recommendations/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch recommendation action history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * List my recommendation action history for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listMyRecommendationActionHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/my-recommendations/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my recommendation action history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get my recommendation action history detail for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getMyRecommendationActionHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/my-recommendations/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my recommendation action history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  // ==================== FOLLOW-UP HISTORY ====================
  
  /**
   * List follow-up history for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listFollowUpHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/followups/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch follow-up history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get follow-up history detail for AuditManager
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getFollowUpHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/followups/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch follow-up history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * List my follow-up history for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {Object} params - Query parameters (search, action_type, ordering, page, page_size)
   */
  async listMyFollowUpHistory(accessToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.action_type) queryParams.append('action_type', params.action_type);
      if (params.ordering) queryParams.append('ordering', params.ordering);
      if (params.page) queryParams.append('page', params.page);
      if (params.page_size) queryParams.append('page_size', params.page_size);

      const response = await fetch(`${AUDIT_BASE_URL}/history/my-followups/?${queryParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my follow-up history',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }

  /**
   * Get my follow-up history detail for AuditUser
   * @param {string} accessToken - JWT access token
   * @param {number} historyId - History record ID
   */
  async getMyFollowUpHistoryDetail(accessToken, historyId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/history/my-followups/${historyId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch my follow-up history detail',
          errorData: data,
        };
      }

      return {
        success: true,
        status: response.status,
        data,
      };
    } catch (error) {
      return {
        success: false,
        status: 0,
        error: error.message || 'Network error occurred',
        errorData: null,
      };
    }
  }
}


const auditHistoryService = new AuditHistoryService();
export default auditHistoryService;